import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Camera, Upload, Scan, AlertTriangle, CheckCircle2, Bug, Crown, Loader2, X, Info, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const FrameScanDialog = ({ open, onClose, token, hives = [], inspectionId = '', onScanComplete, onStartTreatment }) => {
  const [selectedHive, setSelectedHive] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 10MB');
      return;
    }

    // Read and resize image
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Resize if needed
        const maxSize = 1920;
        let width = img.width;
        let height = img.height;
        
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else {
            width = (width / height) * maxSize;
            height = maxSize;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Get base64 without prefix
        const base64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
        setImageData(base64);
        setImagePreview(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (!imageData) {
      toast.error('Please upload an image first');
      return;
    }

    setScanning(true);
    setResult(null);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/api/frame-scan/analyze`, {
        image_base64: imageData,
        hive_id: selectedHive?.id || '',
        hive_number: selectedHive?.hive_number || '',
        inspection_id: inspectionId
      }, { headers });

      setResult(response.data);
      toast.success('Frame analyzed successfully!');
      onScanComplete?.(response.data);
    } catch (err) {
      toast.error('Failed to analyze frame. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleClose = () => {
    setImageData(null);
    setImagePreview(null);
    setResult(null);
    setSelectedHive(null);
    onClose();
  };

  const getBroodPatternColor = (pattern) => {
    switch (pattern) {
      case 'excellent': return 'bg-green-100 text-green-800 border-green-300';
      case 'good': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'spotty': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'poor': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMiteColor = (level) => {
    switch (level) {
      case 'none': return 'text-green-600';
      case 'low': return 'text-blue-600';
      case 'moderate': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scan className="h-5 w-5 text-blue-600" />
            AI Frame Scanner
          </DialogTitle>
          <DialogDescription>
            Upload a photo of your brood frame for AI-powered health analysis
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            {/* Hive Selection */}
            <div>
              <Label>Select Hive (optional)</Label>
              <Select 
                value={selectedHive?.id || 'none'} 
                onValueChange={(v) => setSelectedHive(v === 'none' ? null : hives.find(h => h.id === v))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Associate with a hive..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No hive selected</SelectItem>
                  {hives.map(hive => (
                    <SelectItem key={hive.id} value={hive.id}>
                      #{hive.hive_number} - {hive.yard || 'Unknown yard'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Image Upload Area */}
            <div 
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
                ${imagePreview ? 'border-blue-300 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleFileSelect}
              />
              
              {imagePreview ? (
                <div className="relative">
                  <img 
                    src={imagePreview} 
                    alt="Frame preview" 
                    className="max-h-64 mx-auto rounded-lg shadow-md"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImageData(null);
                      setImagePreview(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Camera className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-lg font-medium text-gray-700">Upload Frame Photo</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Click to browse or drag and drop
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    JPEG, PNG, or WebP • Max 10MB
                  </p>
                </>
              )}
            </div>

            {/* Tips */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex gap-2">
                <Info className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Tips for best results:</p>
                  <ul className="mt-1 space-y-1 text-amber-700">
                    <li>• Take photo in good lighting, avoid shadows</li>
                    <li>• Hold camera ~8-12 inches from frame</li>
                    <li>• Capture the full frame or focus on brood area</li>
                    <li>• Ensure image is clear and not blurry</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Scan Button */}
            <Button
              onClick={handleScan}
              disabled={!imageData || scanning}
              className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg"
            >
              {scanning ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Analyzing Frame...
                </>
              ) : (
                <>
                  <Scan className="h-5 w-5 mr-2" />
                  Analyze Frame
                </>
              )}
            </Button>
          </div>
        ) : (
          /* Results View */
          <div className="space-y-4">
            {/* Summary Card */}
            <div className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-xl p-5 border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Analysis Results</h3>
                <Badge className={getBroodPatternColor(result.brood_pattern)}>
                  {result.brood_pattern?.charAt(0).toUpperCase() + result.brood_pattern?.slice(1)} Pattern
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Brood Coverage */}
                <div className="bg-white rounded-lg p-3">
                  <p className="text-sm text-gray-600">Brood Coverage</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {result.brood_coverage_percent || 0}%
                  </p>
                </div>

                {/* Population */}
                <div className="bg-white rounded-lg p-3">
                  <p className="text-sm text-gray-600">Colony Strength</p>
                  <p className="text-2xl font-bold text-blue-600 capitalize">
                    {result.population_estimate || 'Unknown'}
                  </p>
                </div>
              </div>
            </div>

            {/* Mite Assessment */}
            <div className="bg-white rounded-xl p-4 border">
              <h4 className="font-medium flex items-center gap-2 mb-3">
                <Bug className="h-4 w-4 text-red-600" />
                Mite Assessment
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Visibility</p>
                  <p className={`font-semibold capitalize ${getMiteColor(result.mite_visibility)}`}>
                    {result.mite_visibility || 'Not assessed'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Estimated Load</p>
                  <p className={`font-semibold capitalize ${
                    result.estimated_mite_load === 'critical' ? 'text-red-600' :
                    result.estimated_mite_load === 'concerning' ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {result.estimated_mite_load || 'Not assessed'}
                  </p>
                </div>
              </div>
              
              {/* Treatment Recommendation - Shows when mites are concerning or critical */}
              {(result.estimated_mite_load === 'concerning' || result.estimated_mite_load === 'critical' || 
                result.mite_visibility === 'moderate' || result.mite_visibility === 'high') && onStartTreatment && (
                <div className={`mt-4 p-4 rounded-lg border-2 ${
                  result.estimated_mite_load === 'critical' || result.mite_visibility === 'high'
                    ? 'bg-red-50 border-red-300'
                    : 'bg-amber-50 border-amber-300'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${
                      result.estimated_mite_load === 'critical' || result.mite_visibility === 'high'
                        ? 'bg-red-100'
                        : 'bg-amber-100'
                    }`}>
                      <Zap className={`h-5 w-5 ${
                        result.estimated_mite_load === 'critical' || result.mite_visibility === 'high'
                          ? 'text-red-600'
                          : 'text-amber-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h5 className={`font-semibold ${
                        result.estimated_mite_load === 'critical' || result.mite_visibility === 'high'
                          ? 'text-red-800'
                          : 'text-amber-800'
                      }`}>
                        {result.estimated_mite_load === 'critical' || result.mite_visibility === 'high'
                          ? '⚠️ Immediate Treatment Recommended'
                          : '🔔 Consider Starting Treatment'}
                      </h5>
                      <p className={`text-sm mt-1 ${
                        result.estimated_mite_load === 'critical' || result.mite_visibility === 'high'
                          ? 'text-red-700'
                          : 'text-amber-700'
                      }`}>
                        {result.estimated_mite_load === 'critical' || result.mite_visibility === 'high'
                          ? 'High mite levels detected. Start a treatment plan now to protect your colony.'
                          : 'Mite activity detected. A treatment plan may help prevent colony damage.'}
                      </p>
                      <Button
                        onClick={() => {
                          onStartTreatment({
                            hive: selectedHive,
                            scanResult: result,
                            urgency: result.estimated_mite_load === 'critical' || result.mite_visibility === 'high' ? 'high' : 'medium'
                          });
                          handleClose();
                        }}
                        className={`mt-3 ${
                          result.estimated_mite_load === 'critical' || result.mite_visibility === 'high'
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-amber-600 hover:bg-amber-700'
                        }`}
                      >
                        <Bug className="h-4 w-4 mr-2" />
                        Start Treatment Plan
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Queen Cells */}
            {result.queen_cells_detected && (
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                <h4 className="font-medium flex items-center gap-2 text-purple-800">
                  <Crown className="h-4 w-4" />
                  Queen Cells Detected
                </h4>
                <p className="text-sm text-purple-700 mt-1">
                  {result.queen_cells_count || 'Multiple'} queen cell(s) found
                  {result.analysis?.queen_cells_type && ` (${result.analysis.queen_cells_type})`}
                </p>
              </div>
            )}

            {/* Diseases */}
            {result.diseases_detected?.length > 0 && (
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <h4 className="font-medium flex items-center gap-2 text-red-800 mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  Potential Issues Detected
                </h4>
                <div className="space-y-2">
                  {result.diseases_detected.map((disease, i) => (
                    <div key={i} className="flex items-center justify-between bg-white p-2 rounded">
                      <span className="font-medium text-red-700">{disease}</span>
                      {result.disease_confidence?.[disease] && (
                        <Badge variant="outline" className="text-red-600">
                          {Math.round(result.disease_confidence[disease] * 100)}% confidence
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Positive Signs */}
            {result.analysis?.positive_signs?.length > 0 && (
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <h4 className="font-medium flex items-center gap-2 text-green-800 mb-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Positive Signs
                </h4>
                <ul className="list-disc list-inside text-sm text-green-700 space-y-1">
                  {result.analysis.positive_signs.map((sign, i) => (
                    <li key={i}>{sign}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations */}
            {result.recommendations?.length > 0 && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">Recommendations</h4>
                <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                  {result.recommendations.map((rec, i) => (
                    <li key={i}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Frame Contents */}
            {result.analysis?.frame_contents?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-600">Frame contents:</span>
                {result.analysis.frame_contents.map((content, i) => (
                  <Badge key={i} variant="outline" className="capitalize">
                    {content.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" className="flex-1" onClick={handleClose}>
                Close
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  setResult(null);
                  setImageData(null);
                  setImagePreview(null);
                }}
              >
                Scan Another
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FrameScanDialog;
